import { computed, inject, Injectable, signal } from '@angular/core';
import { IGradingTemplateDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { CoreGradingTemplateService } from '@lb/api/core/services';
import { IEnrollmentDetailItem } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { EnrollmentMemberRole, IEnrollmentMember } from '@lb/api/mylb/interfaces';
import { EnrollmentDetailService } from '@lb/api/mylb/services';
import { EnrollmentMemberService } from '@lb/api/mylb/services/enrollment-member.service';
import { take } from 'rxjs';
import { ResourceErrorType } from '../resource-error/resource-error.component';
import { toResourceErrorType } from '../resource-error/resource-error.util';

@Injectable()
export class EnrollmentDetailStateService {
	private readonly enrollmentDetailService = inject(EnrollmentDetailService);
	private readonly coreGradingTemplateService = inject(CoreGradingTemplateService);
	private readonly enrollmentMemberService = inject(EnrollmentMemberService);

	private readonly _enrollmentID = signal<number | null>(null);
	private readonly _detail = signal<IEnrollmentDetailItem | null>(null);
	private readonly _gradingTemplate = signal<IGradingTemplateDetail | null>(null);
	private readonly _loading = signal(false);
	private readonly _loaded = signal(false);
	private readonly _error = signal(false);
	private readonly _errorType = signal<ResourceErrorType | null>(null);
	private readonly _members = signal<IEnrollmentMember[]>([]);
	private readonly _membersLoaded = signal(false);

	enrollmentID = computed(() => this._enrollmentID());
	enrollmentDetail = computed(() => this._detail());
	gradingTemplate = computed(() => this._gradingTemplate());
	semesters = computed(() => this._detail()?.semesters ?? []);
	loading = computed(() => this._loading());
	loaded = computed(() => this._loaded());
	error = computed(() => this._error());

	/** Chirp room roster (members-ever). Loaded once per shell by loadMembers; shared by the Members tab and the tab-label count. */
	members = computed(() => this._members());
	membersLoaded = computed(() => this._membersLoaded());
	/**
	 * Active LEARNER members only — substitutes (RoleID 6) also live on the roster but are deliberately
	 * not surfaced as members yet, so counting them would show a number the Members tab can't explain.
	 * A missing roleID is treated as Learner, matching the column's DB default.
	 */
	activeMemberCount = computed(
		() =>
			this._members().filter(
				(member) => member.isActive && (member.roleID ?? EnrollmentMemberRole.Learner) === EnrollmentMemberRole.Learner
			).length
	);

	/**
	 * Fetches the roster once for authorized viewers (the shell gates callers). Errors — including an
	 * out-of-scope 403 — degrade to an empty roster, matching every other roster surface.
	 */
	loadMembers(enrollmentID: number): void {
		if (!enrollmentID || this._membersLoaded()) {
			return;
		}

		this.enrollmentMemberService
			.getMembers(enrollmentID)
			.pipe(take(1))
			.subscribe({
				next: (members) => {
					this._members.set(members);
					this._membersLoaded.set(true);
				},
				error: () => {
					this._members.set([]);
					this._membersLoaded.set(true);
				}
			});
	}

	/**
	 * Typed reason the enrollment can't be shown (malformed id / 404 / 403 / other), or null when healthy.
	 * Falls back to `not-found` for the loaded-but-empty case so the shell never strands the user.
	 */
	resourceError = computed<ResourceErrorType | null>(() => {
		const typed = this._errorType();
		if (typed) return typed;
		if (this._loaded() && !this._detail()) return 'not-found';
		return null;
	});

	loadEnrollmentDetail(enrollmentID: number, bustCache = false): void {
		if (!enrollmentID) {
			return;
		}

		const alreadyLoaded = this._loaded() && this._enrollmentID() === enrollmentID;
		if (alreadyLoaded && !bustCache) {
			return;
		}

		this._enrollmentID.set(enrollmentID);
		this._loading.set(true);
		this._loaded.set(false);
		this._error.set(false);
		this._errorType.set(null);

		this.enrollmentDetailService
			.getEnrollmentDetail(enrollmentID, bustCache)
			.pipe(take(1))
			.subscribe({
				next: (detail) => {
					this._detail.set(detail);
					this._loading.set(false);
					this._loaded.set(true);

					if (detail.gradingTemplateID) {
						this.loadGradingTemplate(detail.gradingTemplateID);
					}
				},
				error: (error) => {
					this._detail.set(null);
					this._loading.set(false);
					this._loaded.set(true);
					this._error.set(true);
					this._errorType.set(toResourceErrorType(error));
				}
			});
	}

	/**
	 * Re-fetches the detail in place after a write (e.g. final grade submission) without toggling
	 * `loading` — the overview swaps to a full-page loader on `loading`, which would tear down the page
	 * the user is looking at. Failures keep the current detail rather than blanking it.
	 */
	refreshEnrollmentDetail(enrollmentID: number): void {
		if (!enrollmentID) {
			return;
		}

		this.enrollmentDetailService
			.getEnrollmentDetail(enrollmentID, true)
			.pipe(take(1))
			.subscribe({
				next: (detail) => {
					const templateChanged = detail.gradingTemplateID !== this._detail()?.gradingTemplateID;
					this._detail.set(detail);

					if (templateChanged && detail.gradingTemplateID) {
						this.loadGradingTemplate(detail.gradingTemplateID);
					}
				},
				error: (error) => {
					console.error('Error refreshing enrollment detail:', error);
				}
			});
	}

	/** Flag a malformed enrollment id from the route (e.g. /enrollments/abc) without an API round-trip. */
	markInvalid(): void {
		this._enrollmentID.set(null);
		this._detail.set(null);
		this._loading.set(false);
		this._loaded.set(true);
		this._error.set(true);
		this._errorType.set('invalid');
	}

	private loadGradingTemplate(gradingTemplateID: number): void {
		this._gradingTemplate.set(null);
		this.coreGradingTemplateService
			.getGradingTemplateByID(gradingTemplateID)
			.pipe(take(1))
			.subscribe({
				next: (template) => {
					this._gradingTemplate.set(template);
				},
				error: (error) => {
					console.error('Error loading grading template:', error);
					this._gradingTemplate.set(null);
				}
			});
	}
}
